import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ALL_OPTION, parseParams } from 'hooks/useMachineFilter/utils';
import { useTranslation } from 'react-i18next';
import { TRANSLATION_NAMESPACES } from 'util/translation/constant';
import { PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'util/config/constant';
import { ExpandedArrowIcon } from 'components/UI/ExpandedArrowIcon';
import { BaseTable } from '../../../UI/BaseTable';
import { NotificationExpandable } from '../NotificationExpandable';
import { NotificationFilter } from '../NotificationFilter';
import { STATE_PROP } from '../util/constant';
import { RESET_NOTIFICATION_LIST_STATE } from './state/action';
import { fetchNotificationList } from './state/operation';
import * as selector from './state/selector';
import { renderColumns } from './util/render-columns';

export const NotificationList = () => {
  const { t } = useTranslation(TRANSLATION_NAMESPACES.NOTIFICATION);
  const dispatch = useDispatch();
  const location = useLocation();
  const [defaultExpandedRowKeys] = useState(
    location.state?.currentExpandedRowId
      ? [location.state.currentExpandedRowId]
      : []
  );

  const machineObj = useMemo(() => {
    let defaultMachine = {};
    if (location.state?.defaultMachine) {
      defaultMachine = location.state.defaultMachine;
      return parseParams(defaultMachine.data);
    }
    return parseParams(defaultMachine);
  }, [location.state]);

  const [defaultFilterState, setDefaultFilterState] = useState({
    facility: machineObj.facility,
    object: machineObj.object,
    machineId: machineObj.id !== undefined ? machineObj.id : ALL_OPTION,
    machineIds: machineObj?.id ? [machineObj.id] : [],
    severity: location.state?.severities ? location.state?.severities : [],
    type: ''
  });

  const { notificationsInfo } = useSelector((state) => ({
    notificationsInfo: selector.selectNotificationListInfo(state)
  }));

  const columns = useMemo(() => {
    return renderColumns(t);
  }, [t]);

  const [pagination, setPagination] = useState({
    page: location.state?.page ? location.state.page : 1,
    limit: PAGE_SIZE,
    stateProp: STATE_PROP
  });

  const handlePageChange = (page) => {
    setPagination((currentPagination) => ({
      ...currentPagination,
      page
    }));
  };

  const handlePageLimit = (limit) => {
    setPagination((currentPagination) => ({
      ...currentPagination,
      page: 1,
      limit
    }));
  };

  const tableProps = useMemo(
    () => ({
      columns,
      loading: notificationsInfo.isLoading,
      dataSource: notificationsInfo.data,
      rowKey: 'id',
      pagination,
      expandable: {
        expandedRowRender: (rowData) => {
          return <NotificationExpandable rowData={rowData} />;
        },
        expandIcon: ExpandedArrowIcon,
        expandIconColumnIndex: 8,
        defaultExpandedRowKeys,
        expandRowByClick: true
      },
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      showRowsPerPage: true,
      onPageChange: handlePageChange,
      onLimitChange: handlePageLimit,
      className: 'table-yellow-header'
    }),
    [
      columns,
      defaultExpandedRowKeys,
      notificationsInfo.data,
      notificationsInfo.isLoading,
      pagination
    ]
  );

  const handleChange = useCallback((newFilters) => {
    setDefaultFilterState(newFilters);
  }, []);

  useEffect(() => {
    return dispatch(
      fetchNotificationList({
        limit: pagination.limit,
        page: pagination.page,
        filters: defaultFilterState
      })
    );
  }, [dispatch, pagination.limit, pagination.page, defaultFilterState]);

  useEffect(
    () => () => {
      dispatch({
        type: RESET_NOTIFICATION_LIST_STATE
      });
    },
    [dispatch]
  );

  return (
    <div className="notifications-table">
      <NotificationFilter
        filters={defaultFilterState}
        onChange={handleChange}
      />
      <BaseTable {...tableProps} />
    </div>
  );
};
